/**
 * A company's receivables settings (docs/tally/18 §7–§10): how Timely groups
 * and labels what TallyPrime reports — never what it reports.
 *
 * Read through the database functions of migration 0036, as the person
 * signed in: any member may read them (every screen that ages money needs
 * them). Saved through the server (POST /api/receivables/settings), because a
 * save also rewrites the book's ageing for every TallyPrime account in the
 * same transaction (docs/tally/18 §1.3): `datasource.manage` saves them, as a
 * new version, audited. A database without 0036 has no such functions: the
 * screens then use the product's standard settings and offer no save
 * (`available: false`), rather than a switch that does nothing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_RECEIVABLES_SETTINGS, validateReceivablesSettings, type ReceivablesSettings } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { ApiError, apiPost } from '../lib/api';

export interface SettingsVersion {
    version: number;
    settings: ReceivablesSettings;
    changedAt: string | null;
    changedBy: string | null;
    reason?: string | null;
    summary?: string[];
}

export interface ReceivablesSettingsState {
    loading: boolean;
    /** False when the database has no receivables settings yet (before migration 0036). */
    available: boolean;
    current: SettingsVersion;
    error: string | null;
}

const STANDARD: SettingsVersion = { version: 0, settings: DEFAULT_RECEIVABLES_SETTINGS, changedAt: null, changedBy: null };

/** The function is not in this database: an older migration, not an error to show. */
export const isMissingFunction = (error: { code?: string } | null): boolean => Boolean(error && (error.code === 'PGRST202' || error.code === '42883'));

export function useReceivablesSettings(workspaceId: string) {
    const [state, setState] = useState<ReceivablesSettingsState>({ loading: true, available: false, current: STANDARD, error: null });
    const issuedFor = useRef(workspaceId);

    const reload = useCallback(async () => {
        issuedFor.current = workspaceId;
        const { data, error } = await supabase().rpc('receivables_settings_get', { p_workspace: workspaceId });
        if (issuedFor.current !== workspaceId) return;
        if (error) {
            setState(isMissingFunction(error)
                ? { loading: false, available: false, current: STANDARD, error: null }
                : { loading: false, available: true, current: STANDARD, error: 'The receivables settings could not be read just now.' });
            return;
        }
        const row = data as { version?: number; settings?: unknown; changedAt?: string | null; changedBy?: string | null } | null;
        const valid = row ? validateReceivablesSettings(row.settings).settings : null;
        setState({
            loading: false, available: true, error: null,
            current: row && valid && typeof row.version === 'number'
                ? { version: row.version, settings: valid, changedAt: row.changedAt ?? null, changedBy: row.changedBy ?? null }
                : STANDARD,
        });
    }, [workspaceId]);

    useEffect(() => { void reload(); }, [reload]);

    /**
     * Saves a new version, and with it the book's ageing for every TallyPrime
     * account. Refused, nothing saved, if someone else saved meanwhile or
     * TallyPrime updated since the screen read it: the server says which.
     */
    const save = useCallback(async (settings: ReceivablesSettings, reason: string, impact: Record<string, unknown>): Promise<number> => {
        try {
            const saved = await apiPost<{ version: number }>('/api/receivables/settings', {
                workspaceId, expectedVersion: state.current.version, settings, reason: reason.trim() || null, impact,
            });
            await reload();
            return saved.version;
        } catch (e) {
            throw new Error(e instanceof ApiError ? e.message : 'The settings could not be saved just now. Nothing was changed.');
        }
    }, [workspaceId, state.current.version, reload]);

    return { ...state, reload, save };
}

export async function loadSettingsHistory(workspaceId: string): Promise<SettingsVersion[]> {
    const { data, error } = await supabase().rpc('receivables_settings_history', { p_workspace: workspaceId });
    if (error) throw new Error(isMissingFunction(error) ? 'There is no settings history yet.' : 'The settings history could not be read just now.');
    return ((data ?? []) as SettingsVersion[]);
}
